import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  LockKeyhole,
  MapPin,
  Minus,
  PackageCheck,
  Plus,
  ShieldCheck,
  ShoppingCart,
  Truck,
} from "lucide-react";
import { api } from "../services/api";
import { useAuth } from "../auth/AuthContext";
import {
  calculateSummary,
  clearCartItem,
  formatMoney,
  getCartImage,
  readCartItem,
  readCartItems,
  readCheckoutDraft,
  readConfirmedOrder,
  writeCartItems,
  writeCheckoutDraft,
  writeConfirmedOrder,
} from "../utils/checkoutCart";

const steps = ["Cart", "Checkout", "Payment", "Thank you"];
const MIN_CART_QTY = 1;
const MAX_CART_QTY = 99;

function FlowShell({ activeStep, title, subtitle, children, aside }) {
  return (
    <div className="min-h-screen bg-white text-black">
      <main className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <div className="mb-10 flex flex-col gap-6">
          <Link to="/all-products" className="inline-flex w-fit items-center gap-2 text-xs font-black uppercase tracking-widest text-sky-500 hover:text-sky-600">
            <ArrowLeft size={16} />
            Continue shopping
          </Link>

          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="mb-2 text-xs font-black uppercase tracking-[0.24em] text-sky-400">Bobbin USA Checkout</p>
              <h1 className="text-4xl font-black tracking-tight md:text-5xl">{title}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500">{subtitle}</p>
            </div>

            <div className="grid grid-cols-4 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              {steps.map((step, index) => {
                const isActive = index <= activeStep;
                return (
                  <div key={step} className={`min-w-[76px] px-3 py-3 text-center text-[10px] font-black uppercase tracking-widest ${isActive ? "bg-sky-50 text-sky-500" : "text-gray-400"} ${index !== steps.length - 1 ? "border-r border-gray-200" : ""}`}>
                    {step}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {aside ? (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <section className="lg:col-span-8">{children}</section>
            <aside className="lg:col-span-4">{aside}</aside>
          </div>
        ) : (
          <section>{children}</section>
        )}
      </main>
    </div>
  );
}

function OrderSummary({ cta, item, items, summary, shippingLabel = "USPS Ground Free" }) {
  const displayItems = items || (item ? [item] : []);
  return (
    <div className="sticky top-40 rounded-3xl border border-gray-100 bg-white/80 p-6 shadow-lg backdrop-blur">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-black tracking-tight">Order summary</h2>
        <ShieldCheck className="text-sky-400" size={22} />
      </div>

      {displayItems.length ? (
        <div className="space-y-4 border-b border-gray-100 pb-5">
          {displayItems.map((displayItem, index) => (
            <div className="flex gap-4" key={`${displayItem.sku}-${displayItem.variantId || "base"}-${index}`}>
              <div className="h-20 w-20 flex-shrink-0 rounded-2xl border border-gray-100 bg-white p-2 shadow-sm">
                <img src={getCartImage(displayItem)} alt={displayItem.name} className="h-full w-full object-contain" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-black leading-5">{displayItem.name}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-widest text-gray-400">
                  Qty {displayItem.quantity} - {displayItem.condition || "New"}
                </p>
                <OptionSummary options={displayItem.selectedOptions} />
                <p className="mt-2 text-sm font-black text-sky-500">
                  {formatMoney(Number(displayItem.price || 0) * Number(displayItem.quantity || 1))}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border-b border-gray-100 pb-5">
          <p className="rounded-2xl bg-gray-50 p-4 text-sm font-bold text-gray-500">No item in cart.</p>
        </div>
      )}

      <div className="space-y-3 border-b border-gray-100 py-5 text-sm">
        <SummaryLine label="Subtotal" value={formatMoney(summary.subtotal)} />
        {Number(summary.discount || 0) > 0 && (
          <SummaryLine label={`Customer discount${summary.discountPercent ? ` (${summary.discountPercent}%)` : ""}`} value={`-${formatMoney(summary.discount)}`} highlight />
        )}
        <SummaryLine label="Shipping" value={shippingLabel} highlight />
        <SummaryLine label="Estimated tax" value={formatMoney(summary.tax)} />
      </div>

      <div className="flex items-baseline justify-between pt-5">
        <span className="text-xs font-black uppercase tracking-widest text-gray-500">Total</span>
        <span className="text-3xl font-black">{formatMoney(summary.total)}</span>
      </div>

      {cta}

      <div className="mt-5 flex items-center gap-3 rounded-2xl bg-sky-50 p-4 text-xs font-bold leading-5 text-sky-700">
        <LockKeyhole size={18} className="flex-shrink-0" />
        Secure checkout. Payment is created and captured by the backend with PayPal.
      </div>
    </div>
  );
}

function SummaryLine({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-gray-500">{label}</span>
      <span className={`font-black ${highlight ? "text-sky-500" : "text-gray-900"}`}>{value}</span>
    </div>
  );
}

function OptionSummary({ options }) {
  const entries = Object.entries(options || {}).filter(([, value]) => value);
  if (!entries.length) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {entries.map(([name, value]) => (
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-slate-500" key={name}>
          {name}: {value}
        </span>
      ))}
    </div>
  );
}

function Field({ label, name, value, onChange, placeholder, wide, type = "text", required = true }) {
  return (
    <label className={wide ? "md:col-span-2" : ""}>
      <span className="mb-2 block text-[11px] font-black uppercase tracking-widest text-gray-500">{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-4 text-sm font-semibold outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
      />
    </label>
  );
}

function SelectField({ label, name, value, onChange, children, wide }) {
  return (
    <label className={wide ? "md:col-span-2" : ""}>
      <span className="mb-2 block text-[11px] font-black uppercase tracking-widest text-gray-500">{label}</span>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required
        className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-4 text-sm font-semibold outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
      >
        {children}
      </select>
    </label>
  );
}

function InfoCard({ icon, title, text }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white/80 p-5 shadow backdrop-blur">
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-50 text-sky-500">{icon}</div>
      <h3 className="font-black">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-gray-500">{text}</p>
    </div>
  );
}

function useCartItems() {
  const [items, setItems] = useState(() => readCartItems());
  const updateItems = (nextItems) => {
    const normalized = Array.isArray(nextItems) ? nextItems.filter(Boolean) : [];
    setItems(normalized);
    if (normalized.length) writeCartItems(normalized);
    else clearCartItem();
  };
  return [items, updateItems];
}

export function CartPage() {
  const { user } = useAuth();
  const [items, setItems] = useCartItems();
  const discountPercent = Number(user?.customerDiscount?.percent || 0);
  const summary = useMemo(() => calculateSummary(items, 0, discountPercent), [discountPercent, items]);

  if (!items.length) {
    return (
      <FlowShell
        activeStep={0}
        title="View cart"
        subtitle="Your cart is empty. Add a product before checkout."
        aside={<OrderSummary item={null} summary={calculateSummary(null)} shippingLabel="USPS Ground Free" />}
      >
        <div className="rounded-3xl border border-gray-100 bg-white p-8 text-center shadow-lg">
          <ShoppingCart className="mx-auto text-sky-400" size={44} />
          <h2 className="mt-4 text-2xl font-black tracking-tight">Your cart is empty.</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm font-semibold leading-6 text-gray-500">
            Saved cart items appear here after you add a product from the website.
          </p>
          <Link to="/all-products" className="mt-6 inline-flex rounded-3xl bg-sky-300 px-6 py-4 text-xs font-black uppercase tracking-widest shadow transition hover:-translate-y-0.5 hover:brightness-105">
            Shop products
          </Link>
        </div>
      </FlowShell>
    );
  }

  const setQuantity = (index, quantity) => {
    setItems(items.map((item, itemIndex) => (
      itemIndex === index
        ? { ...item, quantity: Math.max(MIN_CART_QTY, Math.min(MAX_CART_QTY, quantity)) }
        : item
    )));
  };

  const removeItem = (index) => {
    setItems(items.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <FlowShell
      activeStep={0}
      title="View cart"
      subtitle="Review selected sewing machine parts, confirm quantities, then continue into checkout."
      aside={
        <OrderSummary
          item={items[0]}
          items={items}
          summary={summary}
          shippingLabel="USPS Ground Free"
          cta={
            <Link to="/checkout" className="mt-6 flex w-full items-center justify-center gap-2 rounded-3xl bg-sky-300 px-6 py-5 text-sm font-black uppercase tracking-widest shadow transition hover:-translate-y-0.5 hover:brightness-105">
              Checkout
              <ShoppingCart size={18} />
            </Link>
          }
        />
      }
    >
      <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-lg md:p-6">
        <div className="space-y-5">
          {items.map((item, index) => (
            <div className="grid gap-6 border-b border-gray-100 pb-5 last:border-b-0 last:pb-0 md:grid-cols-[140px_1fr_auto] md:items-center" key={`${item.sku}-${item.variantId || "base"}-${index}`}>
              <div className="aspect-square rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                <img src={getCartImage(item)} alt={item.name} className="h-full w-full object-contain" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-sky-400">{item.seller}</p>
                <h2 className="mt-2 text-2xl font-black tracking-tight">{item.name}</h2>
                <p className="mt-2 text-sm text-gray-500">SKU: {item.sku}</p>
                <OptionSummary options={item.selectedOptions} />
                <div className="mt-5 flex flex-wrap gap-3">
                  <span className="rounded-full bg-sky-50 px-4 py-2 text-xs font-black uppercase tracking-widest text-sky-500">USPS Ground Free</span>
                  <span className="rounded-full bg-gray-100 px-4 py-2 text-xs font-black uppercase tracking-widest text-gray-600">{item.condition}</span>
                </div>
              </div>
              <div className="flex flex-col gap-5 md:items-end">
                <p className="text-3xl font-black">{formatMoney(Number(item.price || 0) * Number(item.quantity || 1))}</p>
                <div className="flex items-center gap-4 rounded-3xl bg-white px-4 py-3 shadow">
                  <button type="button" onClick={() => setQuantity(index, item.quantity - 1)} disabled={item.quantity <= MIN_CART_QTY} className="rounded-full p-2 text-gray-400 transition hover:bg-sky-50 hover:text-sky-500 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Decrease quantity">
                    <Minus size={16} />
                  </button>
                  <span className="min-w-6 text-center font-black" aria-live="polite">{item.quantity}</span>
                  <button type="button" onClick={() => setQuantity(index, item.quantity + 1)} disabled={item.quantity >= MAX_CART_QTY} className="rounded-full p-2 text-gray-400 transition hover:bg-sky-50 hover:text-sky-500 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Increase quantity">
                    <Plus size={16} />
                  </button>
                </div>
                <button className="text-xs font-black uppercase tracking-widest text-red-500 hover:text-red-600" onClick={() => removeItem(index)} type="button">
                  Remove item
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-3">
        <InfoCard icon={<Truck size={22} />} title="Shipping choices" text="Free USPS Ground plus fixed air options are available at checkout." />
        <InfoCard icon={<PackageCheck size={22} />} title="Ready to ship" text="Paid orders are recorded in the admin order dashboard." />
        <InfoCard icon={<ShieldCheck size={22} />} title="PayPal checkout" text="Payment is handled through secure server-side PayPal capture." />
      </div>
    </FlowShell>
  );
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, setUser } = useAuth();
  const [cartItems] = useCartItems();
  const [shippingMethod, setShippingMethod] = useState("");
  const [shippoRates, setShippoRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState("");
  const [savedAddresses, setSavedAddresses] = useState(user?.addresses || []);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [address, setAddress] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "US",
  });

  const selectedShipping = shippoRates.find((option) => option.objectId === shippingMethod) || shippoRates[0] || { amount: 0, label: "Shippo rates pending" };
  const discountPercent = Number(user?.customerDiscount?.percent || 0);
  const summary = calculateSummary(cartItems, selectedShipping.amount, discountPercent);

  const addressToForm = (savedAddress) => ({
    firstName: String(savedAddress?.fullName || "").split(" ")[0] || "",
    lastName: String(savedAddress?.fullName || "").split(" ").slice(1).join(" ") || "",
    email: user?.email || "",
    phone: savedAddress?.phone || user?.phone || "",
    line1: savedAddress?.line1 || "",
    line2: savedAddress?.line2 || "",
    city: savedAddress?.city || "",
    state: savedAddress?.state || "",
    postalCode: savedAddress?.postalCode || "",
    country: savedAddress?.country || "US",
  });

  useEffect(() => {
    let mounted = true;
    if (!isAuthenticated) return undefined;

    api.get("/auth/me")
      .then((data) => {
        if (!mounted) return;
        const addresses = data.user?.addresses || [];
        setSavedAddresses(addresses);
        setUser(data.user);
        localStorage.setItem("authUser", JSON.stringify(data.user));
        const defaultAddress = addresses.find((item) => item.isDefault) || addresses[0];
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress._id);
          setAddress(addressToForm(defaultAddress));
        } else {
          setAddress((current) => ({ ...current, email: data.user?.email || "", phone: data.user?.phone || "" }));
        }
      })
      .catch(() => {
        setSavedAddresses(user?.addresses || []);
      });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!cartItems.length) {
    return (
      <FlowShell activeStep={1} title="Checkout" subtitle="Add a product to your cart before entering checkout." aside={<OrderSummary item={null} summary={calculateSummary(null)} />}>
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6">
          <h2 className="text-xl font-black text-gray-900">Your cart is empty</h2>
          <p className="mt-2 text-sm font-semibold text-gray-600">Please add a product before continuing to payment.</p>
          <Link to="/all-products" className="mt-5 inline-flex rounded-2xl bg-gray-950 px-5 py-3 text-sm font-black text-white">Shop products</Link>
        </div>
      </FlowShell>
    );
  }

  const updateAddress = (event) => {
    setSelectedAddressId("");
    setAddress((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const selectSavedAddress = (event) => {
    const addressId = event.target.value;
    setSelectedAddressId(addressId);
    const savedAddress = savedAddresses.find((item) => item._id === addressId);
    if (savedAddress) setAddress(addressToForm(savedAddress));
  };

  const canFetchRates = Boolean(address.line1 && address.city && address.state && address.postalCode && address.country);

  const fetchShippoRates = async () => {
    if (!canFetchRates) {
      setRatesError("Complete the shipping address before loading Shippo rates.");
      return;
    }
    setRatesLoading(true);
    setRatesError("");
    setShippoRates([]);
    setShippingMethod("");
    try {
      const data = await api.post("/shippo/checkout-rates", {
        items: cartItems,
        shippingAddress: {
          ...address,
          fullName: `${address.firstName} ${address.lastName}`.trim(),
        },
        parcel: { length: 10, width: 8, height: 4 },
      });
      const nextRates = (data.rates || []).map((rate) => ({
        ...rate,
        label: `${rate.carrier} ${rate.service}`,
        amount: Number(rate.amount || 0),
      }));
      setShippoRates(nextRates);
      setShippingMethod(nextRates[0]?.objectId || "");
      if (!nextRates.length) setRatesError("Shippo did not return rates for this address.");
    } catch (err) {
      setRatesError(err.message);
    } finally {
      setRatesLoading(false);
    }
  };

  const continueToPayment = (event) => {
    event.preventDefault();
    if (!shippingMethod) {
      setRatesError("Select a Shippo shipping rate before continuing.");
      return;
    }
    writeCheckoutDraft({
      item: cartItems[0],
      items: cartItems,
      shippingAddress: {
        ...address,
        fullName: `${address.firstName} ${address.lastName}`.trim(),
      },
      shippingMethod: `shippo:${shippingMethod}`,
      shippoRateId: shippingMethod,
      shippingLabel: selectedShipping.label,
      discountPercent,
      summary,
    });
    navigate("/payment-details");
  };

  return (
    <FlowShell
      activeStep={1}
      title="Checkout"
      subtitle="Enter shipping and contact details before approving payment through PayPal."
      aside={<OrderSummary item={cartItems[0]} items={cartItems} summary={summary} shippingLabel={`${selectedShipping.label} - ${formatMoney(selectedShipping.amount)}`} />}
    >
      <form className="space-y-8" onSubmit={continueToPayment}>
        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-lg">
          <div className="mb-6 flex items-center gap-3">
            <MapPin className="text-sky-400" size={24} />
            <div>
              <h2 className="text-xl font-black">Shipping details</h2>
              {isAuthenticated && <p className="mt-1 text-sm font-semibold text-gray-500">Use a saved address from My Account or enter a new one.</p>}
            </div>
          </div>
          {isAuthenticated && (
            <div className="mb-5 rounded-2xl border border-sky-100 bg-sky-50 p-4">
              <label>
                <span className="mb-2 block text-[11px] font-black uppercase tracking-widest text-sky-700">Saved addresses</span>
                <select
                  className="w-full rounded-xl border border-sky-100 bg-white px-4 py-3 text-sm font-bold outline-none"
                  onChange={selectSavedAddress}
                  value={selectedAddressId}
                >
                  <option value="">Enter a new address</option>
                  {savedAddresses.map((savedAddress) => (
                    <option key={savedAddress._id} value={savedAddress._id}>
                      {savedAddress.label || "Address"}{savedAddress.isDefault ? " (Default)" : ""} - {[savedAddress.line1, savedAddress.city, savedAddress.state, savedAddress.postalCode].filter(Boolean).join(", ")}
                    </option>
                  ))}
                </select>
              </label>
              {!savedAddresses.length && (
                <Link className="mt-3 inline-flex text-xs font-black uppercase tracking-widest text-sky-700" to="/my-account">
                  Add addresses in My Account
                </Link>
              )}
            </div>
          )}
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="First name" name="firstName" value={address.firstName} onChange={updateAddress} placeholder="John" />
            <Field label="Last name" name="lastName" value={address.lastName} onChange={updateAddress} placeholder="Smith" />
            <Field label="Email" name="email" value={address.email} onChange={updateAddress} placeholder="john@example.com" type="email" />
            <Field label="Phone" name="phone" value={address.phone} onChange={updateAddress} placeholder="213-700-2967" />
            <Field label="Street address" name="line1" value={address.line1} onChange={updateAddress} placeholder="123 Sewing Supply Ave" wide />
            <Field label="Address line 2" name="line2" value={address.line2} onChange={updateAddress} placeholder="Suite, unit, etc." wide required={false} />
            <Field label="City" name="city" value={address.city} onChange={updateAddress} placeholder="Beverly Hills" />
            <Field label="State" name="state" value={address.state} onChange={updateAddress} placeholder="CA" />
            <Field label="ZIP code" name="postalCode" value={address.postalCode} onChange={updateAddress} placeholder="90210" />
            <SelectField label="Country" name="country" value={address.country} onChange={updateAddress}>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
            </SelectField>
          </div>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-lg">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-black">Delivery method</h2>
              <p className="mt-1 text-sm text-gray-500">Live carrier rates are loaded from Shippo using the shipping address.</p>
            </div>
            <Truck className="text-sky-400" size={26} />
          </div>
          <button className="mb-4 rounded-2xl bg-gray-950 px-5 py-3 text-sm font-black text-white disabled:opacity-40" disabled={ratesLoading || !canFetchRates} onClick={fetchShippoRates} type="button">
            {ratesLoading ? "Loading Shippo rates..." : "Load Shippo rates"}
          </button>
          {ratesError && <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-800">{ratesError}</div>}
          <div className="grid gap-3">
            {shippoRates.map((option) => (
              <label className={`block rounded-2xl border-2 p-5 ${shippingMethod === option.objectId ? "border-sky-300 bg-sky-50" : "border-gray-100 bg-white"}`} key={option.objectId}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <input checked={shippingMethod === option.objectId} className="mt-1" onChange={() => setShippingMethod(option.objectId)} type="radio" />
                    <div>
                      <p className="font-black">{option.label}</p>
                      <p className="mt-1 text-sm text-gray-500">{option.durationTerms || `ETA ${option.estimatedDays || "N/A"} days`}</p>
                    </div>
                  </div>
                  <span className="font-black text-sky-500">{formatMoney(option.amount)}</span>
                </div>
              </label>
            ))}
            {!shippoRates.length && !ratesLoading && (
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5 text-sm font-bold text-gray-500">
                Complete the address, then load Shippo rates.
              </div>
            )}
          </div>
          <button type="submit" className="mt-6 flex w-full items-center justify-center rounded-3xl bg-sky-300 px-6 py-5 text-sm font-black uppercase tracking-widest shadow transition hover:-translate-y-0.5 hover:brightness-105">
            Continue to payment
          </button>
        </div>
      </form>
    </FlowShell>
  );
}

function PayPalCheckoutButton({ draft, onConfig, onError }) {
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const renderedRef = useRef(false);
  const [status, setStatus] = useState("loading");
  const [localError, setLocalError] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let buttons = null;

    function clearPayPalSdk() {
      document.querySelectorAll('script[src*="paypal.com/sdk/js"]').forEach((script) => script.remove());
      try {
        delete window.paypal;
      } catch {
        window.paypal = undefined;
      }
      if (window.paypal) window.paypal = undefined;
    }

    async function loadScript(clientId, currency, mode) {
      const sdkUrl = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=${encodeURIComponent(currency)}&intent=capture&components=buttons&commit=true`;

      const existing = document.getElementById("paypal-js-sdk");
      if (existing) {
        if (existing.dataset.clientId !== clientId || existing.dataset.currency !== currency || existing.dataset.mode !== mode) {
          clearPayPalSdk();
        } else if (window.paypal?.Buttons) {
          return existing;
        } else {
          return new Promise((resolve, reject) => {
            if (existing.dataset.loaded === "true" && window.paypal?.Buttons) {
              resolve(existing);
              return;
            }

            existing.addEventListener("load", () => resolve(existing), { once: true });
            existing.addEventListener("error", () => reject(new Error("Unable to load PayPal checkout. Please confirm the live PayPal client ID is valid.")), { once: true });
          });
        }
      }

      if (window.paypal && !window.paypal.Buttons) clearPayPalSdk();

      const current = document.getElementById("paypal-js-sdk");
      if (current) {
        return new Promise((resolve, reject) => {
          if (current.dataset.loaded === "true" && window.paypal?.Buttons) {
            resolve(current);
            return;
          }

          current.addEventListener("load", () => resolve(current), { once: true });
          current.addEventListener("error", () => reject(new Error("Unable to load PayPal checkout. Please confirm the live PayPal client ID is valid.")), { once: true });
        });
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.id = "paypal-js-sdk";
        script.src = sdkUrl;
        script.async = true;
        script.dataset.clientId = clientId;
        script.dataset.currency = currency;
        script.dataset.mode = mode;
        script.onload = () => {
          script.dataset.loaded = "true";
          resolve(script);
        };
        script.onerror = () => reject(new Error("Unable to load PayPal checkout. Please confirm the live PayPal client ID is valid."));
        document.body.appendChild(script);
      });
    }

    async function renderButton() {
      try {
        setStatus("loading");
        setLocalError("");
        onError("");
        const config = await api.get("/payments/paypal/config");
        if (!config.enabled) throw new Error("PayPal is not configured on the server");
        onConfig?.(config);
        await loadScript(config.clientId, config.currency, config.mode);
        if (cancelled || renderedRef.current || !buttonRef.current) return;
        if (!window.paypal?.Buttons) throw new Error("PayPal checkout did not initialize. Please refresh and try again.");

        buttonRef.current.innerHTML = "";
        buttons = window.paypal.Buttons({
          style: { layout: "vertical", shape: "pill", label: "pay" },
          createOrder: async () => {
            const data = await api.post("/payments/paypal/create-order", {
              items: draft.items,
              shippingAddress: draft.shippingAddress,
              shippingMethod: draft.shippingMethod,
              shippoRateId: draft.shippoRateId,
            });
            sessionStorage.setItem("bobbinusa_pending_order_id", data.orderId);
            return data.paypalOrderId;
          },
          onApprove: async (data) => {
            const orderId = sessionStorage.getItem("bobbinusa_pending_order_id");
            const capture = await api.post("/payments/paypal/capture-order", {
              paypalOrderId: data.orderID,
              orderId,
            });
            writeConfirmedOrder(capture.order);
            if (capture.shippoLabelError) {
              sessionStorage.setItem("bobbinusa_shippo_label_error", capture.shippoLabelError);
            } else {
              sessionStorage.removeItem("bobbinusa_shippo_label_error");
            }
            clearCartItem();
            navigate("/thank-you");
          },
          onError: (error) => onError(error?.message || "PayPal checkout failed"),
          onCancel: () => onError("PayPal checkout was cancelled"),
        });
        if (typeof buttons.isEligible === "function" && !buttons.isEligible()) {
          throw new Error("PayPal checkout is not eligible for this browser or merchant account. Please check the live PayPal app configuration.");
        }
        renderedRef.current = true;
        await buttons.render(buttonRef.current);
        if (!cancelled) setStatus("ready");
      } catch (error) {
        const message = error?.message || "PayPal checkout failed to load";
        setLocalError(message);
        setStatus("error");
        onError(message);
        renderedRef.current = false;
      }
    }

    renderButton();
    return () => {
      cancelled = true;
      renderedRef.current = false;
      if (buttons?.close) buttons.close().catch?.(() => {});
    };
  }, [draft, navigate, onConfig, onError, retryKey]);

  return (
    <div className="space-y-4">
      {status === "loading" && (
        <div className="rounded-2xl border border-sky-100 bg-sky-50 p-4 text-sm font-bold text-sky-700">
          Loading secure PayPal checkout...
        </div>
      )}
      {localError && (
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm font-bold text-amber-800">
          {localError}
        </div>
      )}
      <div className="min-h-[130px]" ref={buttonRef} />
      {status === "error" && (
        <button
          type="button"
          onClick={() => setRetryKey((value) => value + 1)}
          className="inline-flex rounded-2xl bg-gray-950 px-5 py-3 text-sm font-black text-white hover:bg-gray-800"
        >
          Retry PayPal checkout
        </button>
      )}
    </div>
  );
}

export function PaymentDetailsPage() {
  const draft = useMemo(() => readCheckoutDraft(), []);
  const items = useMemo(() => draft?.items || (draft?.item ? [draft.item] : readCartItems()), [draft]);
  const item = useMemo(() => items[0] || readCartItem(), [items]);
  const summary = useMemo(() => draft?.summary || calculateSummary(items), [draft, items]);
  const [error, setError] = useState("");
  const [paypalConfig, setPaypalConfig] = useState(null);
  const paypalMode = paypalConfig?.mode === "live" ? "live" : "sandbox";

  if (!draft) {
    return (
      <FlowShell activeStep={2} title="Payment details" subtitle="Complete checkout details before opening PayPal." aside={<OrderSummary item={item} items={items} summary={summary} />}>
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6">
          <h2 className="text-xl font-black text-gray-900">Checkout details are missing</h2>
          <p className="mt-2 text-sm font-semibold text-gray-600">Please enter shipping details before starting payment.</p>
          <Link to="/checkout" className="mt-5 inline-flex rounded-2xl bg-gray-950 px-5 py-3 text-sm font-black text-white">Back to checkout</Link>
        </div>
      </FlowShell>
    );
  }

  return (
    <FlowShell
      activeStep={2}
      title="Pay with PayPal"
      subtitle={`Approve the ${paypalMode === "live" ? "live" : "sandbox"} PayPal payment. The server will capture the order and create the admin order record.`}
      aside={<OrderSummary item={item} items={items} summary={summary} shippingLabel={`${draft.shippingLabel} - ${formatMoney(summary.shipping)}`} />}
    >
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-lg">
        <div className="mb-6">
          <p className="text-xs font-black uppercase tracking-[0.24em] text-sky-400">Secure payment</p>
          <h2 className="mt-2 text-2xl font-black">
            PayPal {paypalMode === "live" ? "live" : "sandbox"} checkout
          </h2>
          <p className="mt-2 text-sm leading-6 text-gray-500">
            PayPal opens its own secure approval flow using the merchant {paypalMode === "live" ? "live" : "sandbox"} account.
          </p>
        </div>
        {error && <div className="mb-5 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
        <PayPalCheckoutButton draft={draft} onConfig={setPaypalConfig} onError={setError} />
      </div>
    </FlowShell>
  );
}

export function ThankYouPage() {
  const confirmedOrder = readConfirmedOrder();
  const shippoLabelError = sessionStorage.getItem("bobbinusa_shippo_label_error");
  const item = confirmedOrder?.items?.[0] || null;
  const summary = confirmedOrder
    ? { subtotal: confirmedOrder.subtotal, discount: confirmedOrder.discount, discountPercent: confirmedOrder.discountPercent, shipping: confirmedOrder.shipping, tax: confirmedOrder.tax, total: confirmedOrder.total }
    : calculateSummary(confirmedOrder?.items || item);

  return (
    <FlowShell
      activeStep={3}
      title="Thank you"
      subtitle="Your PayPal payment was captured and the order is now in the admin dashboard."
      aside={
        <OrderSummary
          item={item}
          items={confirmedOrder?.items || (item ? [item] : [])}
          summary={summary}
          shippingLabel={confirmedOrder?.shippingAddress?.shippingLabel || "Shipping"}
          cta={
            <Link to="/track-order" className="mt-6 flex w-full items-center justify-center rounded-3xl bg-sky-300 px-6 py-4 text-xs font-black uppercase tracking-widest shadow transition hover:-translate-y-0.5 hover:brightness-105">
              Track order
            </Link>
          }
        />
      }
    >
      <div className="rounded-3xl border border-gray-100 bg-white p-8 text-center shadow-lg">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-sky-50 text-sky-500">
          <CheckCircle2 size={42} />
        </div>
        <h2 className="mt-6 text-3xl font-black tracking-tight">Your order has been placed.</h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-gray-500">
          {confirmedOrder ? `Order ${confirmedOrder.orderNumber} is paid and ready for processing.` : "Your order confirmation will appear here after payment."}
          Product review will become available in My Account after delivery.
        </p>
        {confirmedOrder?.trackingNumber && (
          <div className="mx-auto mt-5 max-w-xl rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold text-emerald-800">
            Shipping label created automatically. Tracking: {confirmedOrder.carrier ? `${confirmedOrder.carrier} ` : ""}{confirmedOrder.trackingNumber}
          </div>
        )}
        {shippoLabelError && (
          <div className="mx-auto mt-5 max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-800">
            Payment is complete. Shipping label needs admin review: {shippoLabelError}
          </div>
        )}
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/all-products" className="rounded-3xl bg-white px-6 py-4 text-xs font-black uppercase tracking-widest shadow transition hover:-translate-y-0.5">Continue shopping</Link>
          <Link to="/my-account" className="rounded-3xl bg-sky-300 px-6 py-4 text-xs font-black uppercase tracking-widest shadow transition hover:-translate-y-0.5 hover:brightness-105">My account</Link>
        </div>
      </div>
    </FlowShell>
  );
}

export function TrackOrderPage() {
  const confirmedOrder = readConfirmedOrder();
  const [trackForm, setTrackForm] = useState({
    email: confirmedOrder?.shippingAddress?.email || "",
    orderNumber: confirmedOrder?.orderNumber || "",
    trackingNumber: confirmedOrder?.trackingNumber || "",
  });
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingError, setTrackingError] = useState("");
  const [trackingLoading, setTrackingLoading] = useState(false);
  const currentTracking = trackingResult || (confirmedOrder ? {
    orderNumber: confirmedOrder.orderNumber,
    status: confirmedOrder.status,
    paymentStatus: confirmedOrder.paymentStatus,
    trackingNumber: confirmedOrder.trackingNumber,
    carrier: confirmedOrder.carrier,
    shippingLabel: confirmedOrder.shippingAddress?.shippingLabel,
  } : null);
  const shipped = Boolean(currentTracking?.trackingNumber);
  const delivered = currentTracking?.status === "delivered";
  const tracking = [
    ["Order placed", "Payment received and order confirmed."],
    ["Processing", "Bobbin USA is preparing the order."],
    ["Shipped", shipped ? [currentTracking.carrier, currentTracking.trackingNumber].filter(Boolean).join(" ") : "Tracking will appear after fulfillment."],
    ["Delivered", delivered ? "Delivery is complete." : "Delivery confirmation will update here."],
  ];

  const updateTrackForm = (event) => {
    setTrackForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const lookupTracking = async (event) => {
    event.preventDefault();
    setTrackingError("");
    setTrackingResult(null);
    setTrackingLoading(true);
    try {
      const data = await api.post("/orders/track", {
        email: trackForm.email,
        orderNumber: trackForm.orderNumber,
        trackingNumber: trackForm.trackingNumber,
      });
      setTrackingResult(data.tracking);
    } catch (err) {
      setTrackingError(err.message);
    } finally {
      setTrackingLoading(false);
    }
  };

  return (
    <FlowShell activeStep={3} title="Track order" subtitle="Order tracking updates after the admin marks the shipment fulfilled.">
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-lg">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-black">{confirmedOrder?.orderNumber || "Order pending"}</h2>
            <p className="mt-1 text-sm text-gray-500">{confirmedOrder?.shippingAddress?.fullName || "Complete payment to create an order"}</p>
          </div>
          <PackageCheck className="text-sky-400" size={30} />
        </div>
        <form className="mb-6 grid gap-3 rounded-3xl border border-gray-100 bg-gray-50 p-4 md:grid-cols-3" onSubmit={lookupTracking}>
          <input className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold outline-none focus:border-sky-300" name="email" onChange={updateTrackForm} placeholder="Order email" required type="email" value={trackForm.email} />
          <input className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold outline-none focus:border-sky-300" name="orderNumber" onChange={updateTrackForm} placeholder="Order number" value={trackForm.orderNumber} />
          <input className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold outline-none focus:border-sky-300" name="trackingNumber" onChange={updateTrackForm} placeholder="Tracking number" value={trackForm.trackingNumber} />
          <button className="rounded-2xl bg-sky-300 px-5 py-3 text-sm font-black uppercase tracking-widest md:col-span-3" disabled={trackingLoading} type="submit">
            {trackingLoading ? "Checking..." : "Track shipment"}
          </button>
        </form>
        {trackingError && <div className="mb-5 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700">{trackingError}</div>}
        {currentTracking?.trackingNumber && (
          <div className="mb-5 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold text-emerald-800">
            Tracking: {[currentTracking.carrier, currentTracking.trackingNumber].filter(Boolean).join(" ")}
            {currentTracking.shippingLabel && <span className="ml-2 text-emerald-700">/ {currentTracking.shippingLabel}</span>}
          </div>
        )}
        <div className="space-y-4">
          {tracking.map(([title, text], index) => (
            <div key={title} className="flex gap-4 rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full font-black ${(index < 2 || (index === 2 && shipped) || (index === 3 && delivered)) ? "bg-sky-300 text-black" : "bg-gray-100 text-gray-400"}`}>{index + 1}</div>
              <div>
                <h3 className="font-black">{title}</h3>
                <p className="mt-1 text-sm text-gray-500">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </FlowShell>
  );
}
